import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion"; // eslint-disable-line no-unused-vars
import { getSafeLocale } from "../../utils/langUtils";

// ─── Locale imports ───────────────────────────────────────────────────────────
import en from "../../locales/en.json";
import es from "../../locales/es.json";
import ar from "../../locales/ar.json";
import fa from "../../locales/fa.json";
import ru from "../../locales/ru.json";
import tl from "../../locales/tl.json";
import zhCN from "../../locales/zh-CN.json";
import ko from "../../locales/ko.json";
import hy from "../../locales/hy.json";
import hiGuj from "../../locales/hi-Guj.json";

// ─── FAQ question keys (English) ──────────────────────────────────────────────
const FAQ_KEYS = [
  "What is a hospice?",
  "Who pays for a hospice?",
  "Who can receive hospice care?",
  "Where are hospice services received?",
  "Is hospice care giving up?",
  "What happens if a patient's condition improves?",
  "How do you manage pain effectively?",
  "How do I get hospice care for my mom?",
  "What's the right time for hospice care?",
  "How many times will the hospice doctor and nurse need to visit?",
  "Can my doctor continue treating me in hospice?",
  "Will the family still have a voice in hospice care?",
  "What if a loved one needs specialized equipment?",
  "Do you provide hospice in Orange County?",
  "Do I need hospice care if I have home healthcare treatment?",
  "What advantage does hospice care have over a nursing home?",
  "What support is available for the family after the patient passes?",
  "Will hospice provide a hospital bed and medical equipment?",
  "How long can someone stay on hospice?",
  "Does hospice give IV fluids or feeding tubes?",
  "How much does hospice care cost without insurance?",
  "What is the difference between hospice and palliative care?",
  "Where can I find 24/7 hospice support near me?",
];

const LOCALE_MAP = { en, es, ar, fa, ru, tl, "zh-CN": zhCN, ko, hy, "hi-Guj": hiGuj };

// ─── Maps each FAQ question key → exact answer text (from Faq.jsx) ───────────
const FAQ_ANSWER_KEYS = {
  "What is a hospice?":
    "Hospice is a type of specialized care for patients who are nearing the end of their life. Hospice helps patients who are terminally ill live a comfortable life. Patients usually enter hospice during the last few months or weeks of their life. Hospice is personal and family-centered. Hospice provides many kinds of support through a team of highly trained professionals. The goal of hospice is to improve the quality of life and to meet the specific needs of individual patients.\n\nWestlake is an outpatient hospice facility. Hospice care is unique to each patient and includes a wide spectrum of care to manage the patient's physical, emotional, and spiritual needs. Hospice care focuses on the individual as a whole.",

  "Who pays for a hospice?":
    "Westlake Village Hospice is covered by most medical insurances, including Medicare. If you have Medicare, Medicare pays the hospice provider for your care without a deductible. Patients will pay:\n\n• Your monthly Medicare Part A (Hospital Insurance) and Medicare Part B (Medical Insurance) premiums\n• A copayment of up to $5 per prescription for outpatient prescription drugs for pain and symptom management\n• 5% of the Medicare-approved amount for inpatient respite care\n\nPatients without insurance are sometimes eligible to receive financial assistance for hospice care.",

  "Who can receive hospice care?":
    "Hospice is for anyone with a limited life-expectancy. Westlake Village serves patients in the Los Angeles and Ventura area. Patients eligible for hospice usually have a life expectancy of 6 months or less. Medicare requires that patients meet specific guidelines:\n\n• Documented life-limiting prognosis by a certified physician(s), which means a life expectancy of six months, if the disease follows a normal course\n• The patient elects to receive comfort (palliative) care, instead of seeking aggressive treatment\n\nMany of the patients who receive hospice care are elderly.",

  "Where are hospice services received?":
    "The services from hospice care are generally taken to where the person lives. This could be their house, a retirement community, assistance-based communities, etc. They can also receive hospice services at the hospital.\n\nThe Medicare program mandates four levels of care:\n• Regular home hospice care\n• As-needed acute symptom management when in the home\n• 24/7 in-patient care when unable to be addressed in the home\n• Respite inpatient care when it's not possible for the family caregiver to give care for multiple days",

  "Is hospice care giving up?":
    "It's not giving up on your loved one when you or they choose to put them into hospice care. At that point, treatment options will have completely run their course and there will be no other real options moving forward. Hospice care is the best option going forward, when it is chosen. Hospice is not about giving up, but making life better for the individual as the best choice going forward, surrounded by loved ones in their living arrangement.",

  "What happens if a patient's condition improves?":
    "This is a common question and a hopeful one. If a patient's condition improves or stabilizes to the point where they no longer meet the criteria for hospice certification (a prognosis of 6 months or less), they can be discharged from hospice care. They return to their previous medical care plan. There is absolutely no penalty for this. In fact, some patients improve simply because their pain is finally managed, and they are receiving consistent care. If their condition declines again in the future, they can re-enroll in hospice as long as they meet the eligibility criteria.",

  "How do you manage pain effectively?":
    "Pain management is a cornerstone of hospice care. We use a combination of medications (analgesics, anti-inflammatories, opioids) and non-pharmacological therapies (massage, music, positioning) to keep patients comfortable. Our goal is to balance pain relief with alertness, allowing the patient to interact with family as much as possible. Our nurses and doctors are experts in titration — adjusting medication doses carefully to achieve this balance. We also supply an 'Emergency Kit' of medications to the home so that sudden pain can be treated immediately without waiting for a pharmacy.",

  "How do I get hospice care for my mom?":
    "At Westlake Village Hospice, we're here to provide compassionate and personalized care. Here's how:\n\n1. Reach out to us directly: Call us at 818-431-5426 or send a request via our online form to schedule a free consultation\n2. Consult with her doctor: If her doctor agrees that hospice care is the best option, we can work directly with them\n3. Insurance assistance: We provide hospice care 100% covered by Medicare and all other insurances, no out-of-pocket expenses\n4. Comprehensive care team: Our experienced team of doctors, nurses, and caregivers will provide in-home support\n\nWe believe in treating each patient like family, and our goal is to enhance the quality of life.",

  "What's the right time for hospice care?":
    "These services generally begin when the life expectancy is six months or less. These services should be considered when medical treatment has run its course and can't fight the disease(s) anymore, or the symptoms are completely outweighing the benefits of treatment.\n\nSigns a patient may be ready:\n• Multiple ER visits\n• Pain that lingers despite attempts for reduction\n• Consistent rate of infection\n• A quick decline in physical function\n• Loss of weight\n• Being out-of-breath on a semi-regular/regular basis",

  "How many times will the hospice doctor and nurse need to visit?":
    "A hospice nurse will work with the patient and/or family to determine the best plan of action going forward within the hospice system. This will determine how many visits the patient needs from the doctor and nurse(s), and how many the patient will get. Depending on individual patient needs, it could be a few or it could be regular visits.",

  "Can my doctor continue treating me in hospice?":
    "You will be able to continue being seen by your doctor, yes. The hospice doctor and team members will work with your physician to determine the level of care they need to continue giving you as you remain in hospice. This is determined by the doctor.",

  "Will the family still have a voice in hospice care?":
    "They will have a say, yes. In fact, their say drives the hospice plan that is laid out for their loved one. The family is part of the hospice team, so they will always be kept in the loop as well as consulted when questions arise as to the patient's requirements and needs.",

  "What if a loved one needs specialized equipment?":
    "The hospice will provide the equipment needed for the patient. Whether it's an oxygen machine, special chair, etc., it is the hospice that will be acquiring and providing the various equipment that each patient needs. Medication and other medical supplies will also be provided; families will be consulted to determine precisely which supplies the patient needs for their situation.",

  "Do you provide hospice in Orange County?":
    "Yes, we proudly provide hospice services in Orange County! We serve patients and families throughout the region, offering compassionate, personalized care tailored to meet the unique needs of each patient. Our experienced team of hospice professionals, including doctors, nurses, and caregivers, will work with you and your loved ones to ensure comfort and support during this important time.\n\nIf you're looking for quality hospice care in Orange County, please contact us today at 818-431-5426.",

  "Do I need hospice care if I have home healthcare treatment?":
    "The hospice is going to offer many advantages that home healthcare treatment doesn't have. This includes medicines, equipment, and visits from staff that are necessary. Typically, the goal of home healthcare is to help the patient become more independent, and the number of visits will go down as the frequencies of the various symptoms decrease. Hospices' goals are to ensure the full comfort of the patient as their symptoms continue to grow. The service will evolve to fulfill the needs that the patient has.",

  "What advantage does hospice care have over a nursing home?":
    "Nursing homes focus on daily care and daily routines that manage day-to-day living issues for elderly people. Hospice care provides additional help and benefit from dedicated medical teams that go far beyond what a typical nursing home offers.",

  "What support is available for the family after the patient passes?":
    "Hospice care does not end with the passing of the patient. We provide bereavement support for the family for at least 13 months following the loss. This includes individual counseling, support groups, and regular check-ins to help family members navigate the grieving process. We are committed to supporting you through your journey of healing.",

  "Will hospice provide a hospital bed and medical equipment?":
    "Yes. Westlake Village Hospice coordinates the delivery and setup of all necessary medical equipment directly to your home at no cost to you. This includes hospital beds, pressure-relief mattresses to prevent bedsores, oxygen concentrators and portable tanks, wheelchairs and walkers, bedside commodes, nebulizers, and all related medical supplies such as bandages, catheters, and incontinence products. Equipment is typically delivered within hours of a patient's enrollment — we build a safe, clinical environment inside your home so you never have to go to a facility for comfort care.",

  "How long can someone stay on hospice?":
    "Hospice is designed for patients with a prognosis of six months or less if the disease runs its normal course. However, there is no hard time limit — care can be continued indefinitely as long as a physician certifies that the patient continues to meet eligibility criteria. Patients are reassessed every 60 days during the first six months and every 60 days thereafter. If a patient's condition improves and they no longer meet criteria, they are discharged — without penalty — and may re-enroll in the future if their condition declines again.",

  "Does hospice give IV fluids or feeding tubes?":
    "In hospice, the decision about IV fluids and feeding tubes is always individualized and guided by what brings the most comfort — not what prolongs the process. As the body naturally slows down at end of life, the digestive system often stops processing fluids and food efficiently. Forcing nutrition through an IV or tube can sometimes cause discomfort, swelling, or congestion. Our team will have an honest, compassionate conversation with you about the goals of care. If IV fluids are needed for a specific symptom (like medication delivery), we can provide them. The priority is always the patient's comfort and peace.",

  "How much does hospice care cost without insurance?":
    "For the vast majority of patients, hospice care costs nothing out-of-pocket. The Medicare Hospice Benefit (Part A) covers 100% of hospice services — including all medications, medical equipment, nursing visits, aide services, and counseling — related to the terminal diagnosis. Medicaid also covers hospice in full. Most private insurance plans include hospice benefits as well. For patients without any insurance coverage, we will work with you to explore financial assistance programs and potential eligibility for Medicare or Medicaid. We believe financial hardship should never prevent a patient from receiving compassionate end-of-life care.",

  "What is the difference between hospice and palliative care?":
    "Both hospice and palliative care focus on comfort and quality of life, but there are important differences. Palliative care can be received at any stage of a serious illness, alongside curative treatments like chemotherapy or surgery. It is an added layer of support. Hospice care, on the other hand, is a specific form of palliative care for patients who have decided to stop pursuing curative treatment and have a prognosis of six months or less. Hospice is a Medicare benefit that covers all services comprehensively. Think of hospice as a fully covered, all-inclusive comfort-care program, while palliative care is a supportive service that can run in parallel with any treatment.",

  "Where can I find 24/7 hospice support near me?":
    "Westlake Village Hospice provides 24/7 on-call nurse support to all enrolled patients and their families — every single night, every weekend, and every holiday. If your loved one experiences a sudden symptom change at 2 a.m., you call us — not 911. A registered nurse will answer immediately, walk you through what to do, and dispatch a nurse to the home if needed. In addition to phone support, we maintain a rapid-response team serving all of Orange County, Los Angeles County, and Ventura County. You are never alone. Call us anytime at 818-431-5426.",
};

// Build FAQ_MAP: { langCode: [ { question (translated), answer, key (EN) } ] }
// Answers: use locale translation if available (via stable EN key), else serve EN directly.
const FAQ_MAP = {};
Object.entries(LOCALE_MAP).forEach(([lang, dict]) => {
  FAQ_MAP[lang] = FAQ_KEYS.map((key) => {
    const answerEN = FAQ_ANSWER_KEYS[key] || "";
    // Try looking up the translation using the English answer as the key
    // (locale files store EN text as key → translated text as value)
    const translated = answerEN && dict[answerEN];
    return {
      key,
      question: dict[key] || en[key] || key,
      answer: translated || answerEN || "Please call us at 818-431-5426 for assistance.",
    };
  });
});

// ─── Language detection ────────────────────────────────────────────────────────
function detectLanguage(text) {
  if (!text || !text.trim()) return null;

  // Script-based detection (most reliable — any character triggers it)
  if (/[\u0600-\u06FF]/.test(text)) {
    return /[\u067E\u0686\u0698\u06AF]/.test(text) ? "fa" : "ar"; // پ چ ژ گ = Farsi
  }
  if (/[\u0400-\u04FF]/.test(text)) return "ru";
  if (/[\uAC00-\uD7AF]/.test(text)) return "ko";
  if (/[\u4E00-\u9FFF\u3040-\u30FF]/.test(text)) return "zh-CN";
  if (/[\u0530-\u058F]/.test(text)) return "hy";
  if (/[\u0900-\u097F\u0A80-\u0AFF]/.test(text)) return "hi-Guj";

  // Latin script — score each language; threshold = 1 point
  const lower = text.toLowerCase();

  // Spanish: accented chars (3 pts each) + broad single-line keyword bank (1 pt each)
  const ES_WORDS = /\b(hola|gracias|sí|qué|cómo|cuándo|dónde|quién|cuánto|el|la|los|las|un|una|es|son|para|con|del|que|se|me|te|le|hospicio|cuidado|enfermedad|dolor|familia|médico|paciente|seguro|necesito|ayuda|quiero|puede|cuesta|cuál|tiene|tienen|estoy|está|están|hay|hacer|necesita|busco|información|pregunta|servicio|atención|casa|hospital|costos|gratis)\b/g;
  const esScore =
    (text.match(/[áéíóúüñ¿¡]/gi) || []).length * 3 +
    (lower.match(ES_WORDS) || []).length;

  // Tagalog: comprehensive single-line keyword bank
  const TL_WORDS = /\b(ang|ng|sa|mga|na|ay|ito|ko|mo|niya|namin|natin|sila|tayo|opo|po|kayo|hindi|oo|salamat|mahal|gusto|ano|paano|saan|kailan|hospicio|pasyente|tahanan|pamilya|doktor|ospital|tulong)\b/g;
  const tlScore = (lower.match(TL_WORDS) || []).length;

  if (esScore >= 1) return "es";
  if (tlScore >= 2) return "tl";
  return "en";
}

// ─── Keyword shortcuts → FAQ key (handles short 1-3 word queries) ─────────────
const KEYWORD_MAP = [
  { words: ["what is hospice", "define hospice", "hospice meaning", "what hospice"], key: "What is a hospice?" },
  { words: ["who pays", "cost", "precio", "insurance", "medicare", "pay", "price", "free", "covered", "coverage", "deductible", "afford"], key: "How much does hospice care cost without insurance?" },
  { words: ["who can", "eligible", "qualify", "eligibility", "criteria", "who receives", "who gets"], key: "Who can receive hospice care?" },
  { words: ["where", "location", "at home", "facility", "place", "in home", "home"], key: "Where are hospice services received?" },
  { words: ["giving up", "give up", "surrender", "quit", "stop treatment"], key: "Is hospice care giving up?" },
  { words: ["condition improves", "better", "improve", "discharged", "discharg", "gets better", "recover"], key: "What happens if a patient's condition improves?" },
  { words: ["pain", "dolor", "comfort", "suffering", "opioid", "medication", "medicine", "drugs"], key: "How do you manage pain effectively?" },
  { words: ["get hospice", "start hospice", "enroll", "sign up", "admit", "begin", "how do i", "my mom", "my dad", "my parent", "my loved one", "refer"], key: "How do I get hospice care for my mom?" },
  { words: ["right time", "when to", "timing", "when should", "signs", "ready for hospice"], key: "What's the right time for hospice care?" },
  { words: ["visit", "visits", "how often", "frequency", "how many times", "nurse visit", "doctor visit"], key: "How many times will the hospice doctor and nurse need to visit?" },
  { words: ["my doctor", "own doctor", "physician", "continue treatment", "primary care", "keep my doctor"], key: "Can my doctor continue treating me in hospice?" },
  { words: ["family voice", "family say", "family involved", "family decision", "family role"], key: "Will the family still have a voice in hospice care?" },
  { words: ["equipment", "wheelchair", "oxygen", "bed", "hospital bed", "supplies", "device", "walker"], key: "Will hospice provide a hospital bed and medical equipment?" },
  { words: ["orange county", "OC", "irvine", "anaheim", "santa ana"], key: "Do you provide hospice in Orange County?" },
  { words: ["home health", "home healthcare", "home care vs hospice", "difference home"], key: "Do I need hospice care if I have home healthcare treatment?" },
  { words: ["nursing home", "skilled nursing", "SNF", "facility vs hospice"], key: "What advantage does hospice care have over a nursing home?" },
  { words: ["after patient passes", "bereavement", "grief", "after death", "family support after", "mourning"], key: "What support is available for the family after the patient passes?" },
  { words: ["how long", "duration", "length", "time limit", "6 months", "six months", "stay on hospice"], key: "How long can someone stay on hospice?" },
  { words: ["IV fluids", "feeding tube", "tube feeding", "IV", "nutrition", "fluids"], key: "Does hospice give IV fluids or feeding tubes?" },
  { words: ["palliative", "difference palliative", "palliative vs hospice", "hospice vs palliative"], key: "What is the difference between hospice and palliative care?" },
  { words: ["24/7", "24 hours", "night", "anytime", "always available", "on call", "weekend", "emergency", "after hours"], key: "Where can I find 24/7 hospice support near me?" },
];

// ─── Multi-stage FAQ matcher ───────────────────────────────────────────────────
function normalize(str) {
  return str
    .toLowerCase()
    .replace(/[^\w\s\u0600-\u06FF\u0400-\u04FF\uAC00-\uD7AF\u4E00-\u9FFF\u0530-\u058F]/g, " ")
    .trim();
}

function wordOverlapScore(a, b) {
  // Stop words to ignore in matching
  const STOP = new Set(["a", "an", "the", "is", "it", "in", "of", "to", "do", "for", "and", "or", "my", "me", "i", "we", "you", "can", "will", "does"]);
  const words = (s) => new Set(normalize(s).split(/\s+/).filter((w) => w.length > 1 && !STOP.has(w)));
  const aWords = words(a);
  const bWords = words(b);
  if (!aWords.size || !bWords.size) return 0;
  let matches = 0;
  aWords.forEach((w) => { if (bWords.has(w)) matches++; });
  // Score = intersection / smaller set (rewards short precise queries)
  return matches / Math.min(aWords.size, bWords.size);
}

function findBestFaqMatch(userText, lang) {
  const normalizedInput = normalize(userText);
  const langFaqs = FAQ_MAP[lang] || FAQ_MAP["en"];
  const enFaqs = FAQ_MAP["en"];

  // ── Stage 1: Keyword shortcut lookup (handles 1-3 word queries reliably) ──
  for (const { words, key } of KEYWORD_MAP) {
    if (words.some((kw) => normalizedInput.includes(normalize(kw)))) {
      const match = langFaqs.find((f) => f.key === key);
      if (match) return match;
    }
  }

  // ── Stage 2: Word-overlap against translated questions (lowered threshold) ──
  let best = null;
  let bestScore = 0;

  langFaqs.forEach((faq) => {
    const score = wordOverlapScore(userText, faq.question);
    if (score > bestScore) { bestScore = score; best = faq; }
  });

  // Also try English question text (for non-English user queries)
  if (lang !== "en") {
    enFaqs.forEach((faq, i) => {
      const score = wordOverlapScore(userText, faq.question);
      if (score > bestScore) {
        bestScore = score;
        best = langFaqs[i] || enFaqs[i];
      }
    });
  }

  if (bestScore >= 0.12) return best;

  // ── Stage 3: Substring — if any meaningful word in input appears in a question ──
  const inputWords = normalizedInput.split(/\s+/).filter((w) => w.length > 3);
  for (const word of inputWords) {
    for (let i = 0; i < enFaqs.length; i++) {
      if (normalize(enFaqs[i].question).includes(word)) {
        return langFaqs[i] || enFaqs[i];
      }
    }
  }

  return null;
}

// ─── Per-language fallback messages ──────────────────────────────────────────
const FALLBACKS = {
  en: "I'm sorry, I couldn't find an exact answer to your question. For personalized assistance, please call us at **818-791-0611** or **818-431-5426** — we're available 24/7.",
  es: "Lo siento, no encontré una respuesta exacta a tu pregunta. Para asistencia personalizada, llámanos al **818-791-0611** o **818-431-5426** — disponibles las 24 horas.",
  ar: "عذرًا، لم أتمكن من العثور على إجابة دقيقة لسؤالك. للحصول على مساعدة شخصية، يرجى الاتصال بنا على **818-791-0611** أو **818-431-5426** — متاحون على مدار الساعة.",
  fa: "متأسفم، نتوانستم پاسخ دقیقی برای سوال شما پیدا کنم. برای کمک شخصی با ما تماس بگیرید: **818-791-0611** یا **818-431-5426** — ۲۴ ساعته در دسترس هستیم.",
  ru: "Извините, не удалось найти точный ответ на ваш вопрос. Для персональной помощи позвоните нам: **818-791-0611** или **818-431-5426** — мы доступны круглосуточно.",
  tl: "Paumanhin, hindi ko mahanap ang tamang sagot sa iyong tanong. Para sa personal na tulong, tumawag sa amin sa **818-791-0611** o **818-431-5426** — available kami 24/7.",
  "zh-CN": "抱歉，我找不到您问题的确切答案。如需个人帮助，请拨打 **818-791-0611** 或 **818-431-5426** — 我们24小时提供服务。",
  ko: "죄송합니다. 질문에 대한 정확한 답변을 찾지 못했습니다. 개인 지원을 위해 **818-791-0611** 또는 **818-431-5426** 으로 전화해 주세요 — 24시간 이용 가능합니다.",
  hy: "Ներողություն, ես ձեր հարցի ճշգրիտ պատասխանը չի գտնվեց: Անձնական օգնության համար զանգահարեք **818-791-0611** կամ **818-431-5426**:",
  "hi-Guj": "माफ करें, मुझे आपके प्रश्न का सटीक उत्तर नहीं मिला। व्यक्तिगत सहायता के लिए कृपया **818-791-0611** या **818-431-5426** पर कॉल करें।",
};

// ─── Per-language hint: "write in your preferred language" ───────────────────
const LANG_HINTS = {
  en: "Write in your preferred language — I'll understand you 🌐",
  es: "Escribe en tu idioma preferido — te entenderé 🌐",
  ar: "اكتب بلغتك المفضلة — سأفهمك 🌐",
  fa: "به زبان دلخواه خود بنویسید — درک می‌کنم 🌐",
  ru: "Пишите на любом языке — я пойму вас 🌐",
  tl: "Sumulat sa iyong gustong wika — maiintindihan kita 🌐",
  "zh-CN": "用您喜欢的语言书写 — 我能理解您 🌐",
  ko: "원하는 언어로 작성하세요 — 이해합니다 🌐",
  hy: "Գրեք ձեր նախընտրած լեզվով — ես հասկանում եմ 🌐",
  "hi-Guj": "अपनी पसंदीदा भाषा में लिखें — मैं समझूँगा 🌐",
};

const COMMON_Q_LABELS = {
  en: "Common questions:",
  es: "Preguntas frecuentes:",
  ar: "أسئلة شائعة:",
  fa: "سوالات متداول:",
  ru: "Часто задаваемые вопросы:",
  tl: "Mga karaniwang tanong:",
  "zh-CN": "常见问题：",
  ko: "자주 묻는 질문:",
  hy: "Հաճախ տրվող հարցեր:",
  "hi-Guj": "सामान्य प्रश्न:",
};

// ─── Quick-reply chip question keys ──────────────────────────────────────────
const QUICK_CHIP_KEYS = [
  "What is a hospice?",
  "Who pays for a hospice?",
  "Who can receive hospice care?",
  "What's the right time for hospice care?",
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function Chatbot() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";
  const safeLang = getSafeLocale(currentLang);
  const isRTL = currentLang === "ar" || currentLang === "fa";

  const [isOpen, setIsOpen] = useState(false);
  const [detectedLang, setDetectedLang] = useState(currentLang);
  const [chipsUsed, setChipsUsed] = useState(false);

  // Derive RTL from detected lang (updates after user messages)
  const activeLang = LOCALE_MAP[detectedLang] ? detectedLang : "en";
  const isActiveLangRTL = activeLang === "ar" || activeLang === "fa";
  const langHint = LANG_HINTS[activeLang] || LANG_HINTS["en"];
  const commonQLabel = COMMON_Q_LABELS[activeLang] || COMMON_Q_LABELS["en"];

  const [messages, setMessages] = useState([
    {
      id: 1,
      text: t("Hello! How can I help you today?"),
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");

  const sendBotReply = (text) => {
    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, text, sender: "bot", timestamp: new Date() },
    ]);
  };

  const processMessage = (userText) => {
    // Detect language from user's text; fall back to UI lang
    const detected = detectLanguage(userText) || currentLang;
    const resolvedLang = LOCALE_MAP[detected] ? detected : "en";
    setDetectedLang(resolvedLang);

    // Try to match an FAQ
    const match = findBestFaqMatch(userText, resolvedLang);
    if (match) {
      sendBotReply(match.answer);
    } else {
      sendBotReply(FALLBACKS[resolvedLang] || FALLBACKS["en"]);
    }
  };

  const handleSend = (e) => {
    e?.preventDefault();
    const text = inputMessage.trim();
    if (!text) return;

    const userMessage = { id: messages.length + 1, text, sender: "user", timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setChipsUsed(true);

    setTimeout(() => processMessage(text), 600);
  };

  const handleChip = (questionKey) => {
    const lang = LOCALE_MAP[detectedLang] ? detectedLang : "en";
    const faq = FAQ_MAP[lang]?.find((f) => f.key === questionKey);
    const displayQuestion = faq?.question || questionKey;

    const userMessage = { id: messages.length + 1, text: displayQuestion, sender: "user", timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setChipsUsed(true);

    setTimeout(() => {
      if (faq) {
        sendBotReply(faq.answer);
      } else {
        sendBotReply(FALLBACKS[lang] || FALLBACKS["en"]);
      }
    }, 600);
  };

  const formatTime = (timestamp) => {
    try {
      return timestamp.toLocaleTimeString(safeLang, { hour: "2-digit", minute: "2-digit" });
    } catch {
      return timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      <motion.button
        type="button"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed ${isRTL ? "left-6" : "right-6"} bottom-6 z-50 w-16 h-16 bg-[#33528b] hover:bg-[#2a4370] text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300`}
        aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="w-6 h-6" aria-hidden="true" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle className="w-6 h-6" aria-hidden="true" />
            </motion.div>
          )}
        </AnimatePresence>
        {!isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-400 rounded-full border-2 border-white animate-pulse" />
        )}
      </motion.button>

      {/* Chatbot Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="chatbot-title"
            aria-describedby="chatbot-description"
            className={`fixed ${isRTL ? "left-6" : "right-6"} bottom-24 z-50 w-96 max-w-[calc(100vw-3rem)] h-[560px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#33528b] to-[#2a4370] text-white p-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" aria-hidden="true">
                  <Bot className="w-6 h-6" />
                </div>
                <div>
                  <h3 id="chatbot-title" className="font-bold text-lg">{t("Chat Support")}</h3>
                  <p id="chatbot-description" className="text-xs text-blue-100">{t("We're here to help")}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                aria-label="Close chatbot"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {/* Messages */}
            <div className={`flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white ${isRTL ? "text-right" : "text-left"}`}>
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 ${message.sender === "user"
                      ? "bg-[#33528b] text-white"
                      : "bg-gray-200 text-gray-800"
                      }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
                    <span className="text-xs opacity-70 mt-1 block">{formatTime(message.timestamp)}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Language hint banner */}
            <div className={`px-4 py-2 bg-blue-50 border-b border-blue-100 flex-shrink-0 ${isActiveLangRTL ? "text-right" : "text-left"}`}>
              <p className="text-xs text-[#33528b] font-medium leading-snug">{langHint}</p>
            </div>

            {/* Quick-reply chips */}
            {!chipsUsed && (
              <div className="px-4 pb-2 pt-2 flex-shrink-0">
                <p className="text-xs text-gray-500 mb-2 font-medium">{commonQLabel}</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_CHIP_KEYS.map((key) => {
                    const lang = LOCALE_MAP[detectedLang] ? detectedLang : "en";
                    const faq = FAQ_MAP[lang]?.find((f) => f.key === key);
                    const label = faq?.question || key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleChip(key)}
                        className="text-xs bg-blue-50 hover:bg-blue-100 text-[#33528b] border border-blue-200 px-3 py-1.5 rounded-full transition-colors text-left leading-snug"
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSend} className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
              <div className={`flex gap-2 ${isRTL ? "flex-row-reverse" : ""}`}>
                <label htmlFor="chatbot-input" className="sr-only">{t("Type your message")}</label>
                <input
                  type="text"
                  id="chatbot-input"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={t("Type your message...")}
                  aria-label={t("Type your message")}
                  className={`flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[#33528b] transition-colors text-sm ${isRTL ? "text-right" : "text-left"}`}
                />
                <button
                  type="submit"
                  className="w-12 h-12 bg-[#33528b] hover:bg-[#2a4370] text-white rounded-xl flex items-center justify-center transition-colors"
                  aria-label="Send message"
                >
                  <Send className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                {t("For immediate assistance, call 818-791-0611")}
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
